'use client';

import { useEffect, type RefObject } from 'react';

const WIDGET_SCRIPT_URL = 'https://platform.twitter.com/widgets.js';

declare global {
  interface Window {
    twttr?: {
      widgets: {
        createTweet: (
          tweetId: string,
          element: HTMLElement,
          options?: { theme?: 'light' | 'dark' },
        ) => Promise<HTMLElement>;
      };
    };
  }
}

let scriptLoading: Promise<void> | null = null;

function loadTwitterWidgets(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.twttr?.widgets) return Promise.resolve();
  if (scriptLoading) return scriptLoading;

  scriptLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = WIDGET_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Twitter widgets failed to load'));
    document.body.appendChild(script);
  });

  return scriptLoading;
}

export function useArticleTweetEmbeds(
  containerRef: RefObject<HTMLElement | null>,
  contentKey: string,
): void {
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const placeholders = Array.from(
      root.querySelectorAll<HTMLElement>('.article-tweet-embed[data-tweet-id]'),
    );
    if (placeholders.length === 0) return;

    let cancelled = false;

    void loadTwitterWidgets()
      .then(() => {
        if (cancelled || !window.twttr?.widgets) return;

        const theme = document.documentElement.classList.contains('dark')
          ? 'dark'
          : 'light';

        placeholders.forEach((el) => {
          const id = el.getAttribute('data-tweet-id');
          if (!id || el.dataset.tweetRendered === '1') return;
          el.dataset.tweetRendered = '1';
          el.innerHTML = '';
          void window.twttr!.widgets.createTweet(id, el, { theme });
        });
      })
      .catch(() => {
        /* widgets yüklenemezse boş placeholder kalır */
      });

    return () => {
      cancelled = true;
    };
  }, [containerRef, contentKey]);
}
